import {inject, Injectable, InjectionToken} from '@angular/core';
import {webSocket, WebSocketSubject} from 'rxjs/webSocket';
import {Subject, Subscription, timer} from 'rxjs';
import {retry} from 'rxjs/operators';
import {KeycloakService} from './keycloak.service';
import {JsonUtils} from '../utils/json.utils';

export interface Oo8EventServiceConfig {
    serverUrl: string;
    domain: string;
}

export const OO8_EVENT_SERVICE_CONFIG = new InjectionToken<Oo8EventServiceConfig>('oo8-event-service-config');

// ===============================================================
// ===============================================================

@Injectable({
    providedIn: 'root'
})
export class Oo8EventService {
    private readonly RECONNECT_INTERVAL = 5000;
    private readonly RESTORE_MARGIN_DELTA_MS = 200;
    private readonly MAX_RECENT_MESSAGES = 200;

    // ===============================================================
    // ===============================================================

    private readonly _keycloakService: KeycloakService = inject(KeycloakService);
    private readonly _config: Oo8EventServiceConfig = inject(OO8_EVENT_SERVICE_CONFIG);
    private readonly _connectionStatus = new Subject<boolean>();
    private readonly _recentMessageIds = new Set<string>();
    private readonly _subscriptions = new Map<
        string,
        {
            eventSubject: Subject<Oo8EventServiceChannelDataMessage>;
            lastOrTimestampMS: number | null;
            localSubscribeTime: number | null;
        }
    >();
    private _isInitialiazing = false;
    private _isReady = false;
    private _isAuthenticated = false;
    private _socket$: WebSocketSubject<Oo8EventServiceIncommingMessage | Oo8EventServiceOutgoingMessage> | null = null;

    // ===============================================================
    // ===============================================================

    public connectionStatus$ = this._connectionStatus.asObservable();

    // ===============================================================
    // ===============================================================

    public init() {
        if (this._isInitialiazing || this._isReady) {
            return;
        }
        this._isInitialiazing = true;

        this.connect(this._config.serverUrl, this._config.domain);

        this._isInitialiazing = false;
        this._isReady = true;
    }

    // ===============================================================
    // ===============================================================
    /**
     * Subscribes to a channel to receive messages.
     * @param channelName - The name of the channel to subscribe to.
     * @param handler - The Subject that will receive the messages.
     * @param restoreLastMS - (Optional) The duration in milliseconds to retrieve previous messages from.
     */
    public subscribe<T>(
        channelName: string,
        handler: (data: Oo8EventServiceChannelDataMessage, payload: T) => void,
        restoreLastMS: number | null = null
    ): Subscription {
        if (!this._subscriptions.has(channelName)) {
            this._subscriptions.set(channelName, {
                eventSubject: new Subject<Oo8EventServiceChannelDataMessage>(),
                lastOrTimestampMS: restoreLastMS != null ? -restoreLastMS : null,
                localSubscribeTime: Date.now()
            });

            if (this._isAuthenticated) {
                const msg: Oo8EventServiceSubscribeMessage = {
                    type: 'subscribe',
                    channelName,
                    jwtToken: this._keycloakService.getToken()!,
                    lastOrTimestampMs: restoreLastMS != null ? -restoreLastMS : undefined
                };

                this.send(msg);
            }
        }

        const channel = this._subscriptions.get(channelName)!;
        const safeCallback = (data: Oo8EventServiceChannelDataMessage) => {
            try {
                handler(data, data.payload as T);
            } catch (error) {
                console.error('[Oo8EventService] Catched exception while dispatching message:', error);
            }
        };

        return channel.eventSubject.asObservable().subscribe(safeCallback);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Connects to the WebSocket server and sets up the message handling logic.
     * @param url - The URL of the WebSocket server.
     * @param domain - The domain for authentication.
     */
    private connect(url: string, domain: string): void {
        if (this._socket$ && !this._socket$.closed) {
            return;
        }

        this._socket$ = webSocket<Oo8EventServiceIncommingMessage | Oo8EventServiceOutgoingMessage>({
            url,
            deserializer: (e) => JSON.parse(e.data),
            openObserver: {
                next: () => {
                    console.log('[Oo8EventService] Connected to OO8 Event Service');
                    this.authenticate(domain, this._keycloakService.getToken()!);
                }
            },
            closeObserver: {
                next: () => {
                    console.log('[Oo8EventService] Disconnected from OO8 Event Service');
                    this._isAuthenticated = false;
                    this._connectionStatus.next(false);
                }
            }
        });

        this._socket$
            .pipe(
                retry({
                    delay: (err, retryCount) => {
                        console.error('[Oo8EventService] WebSocket error:', err);
                        console.log(`[Oo8EventService] Attempting to reconnect... (${retryCount})`);
                        return timer(this.RECONNECT_INTERVAL);
                    }
                })
            )
            .subscribe({
                next: (value: Partial<Oo8EventServiceIncommingMessage | Oo8EventServiceOutgoingMessage>) => {
                    this.handleMessage(value as Oo8EventServiceIncommingMessage);
                }, // eslint-disable-next-line @typescript-eslint/no-explicit-any
                error: (error: any) => {
                    console.error('[Oo8EventService] WebSocket error after retries:', error);
                },
                complete: () => {
                    console.log('[Oo8EventService] WebSocket connection completed.');
                }
            });
    }
    /**
     * Authenticates the client with the WebSocket server.
     * @param domain - The domain for authentication.
     * @param jwtToken - The JWT token for authentication.
     */
    private authenticate(domain: string, jwtToken: string): void {
        const msg: Oo8EventServiceAuthMessage = {
            type: 'auth',
            domain,
            jwtToken
        };
        this.send(msg);
    }
    /**
     * Handles incoming messages from the WebSocket server.
     * @param msg - The incoming message.
     */
    private handleMessage(msg: Oo8EventServiceIncommingMessage): void {
        switch (msg.type) {
            case 'auth_success':
                console.log('[Oo8EventService] Authenticated:', (msg as Oo8EventServiceSuccessMessage).message);

                this._isAuthenticated = true;
                this._connectionStatus.next(true);
                this._subscriptions.forEach((sub, channelName) => {
                    const msg: Oo8EventServiceSubscribeMessage = {
                        type: 'subscribe',
                        channelName,
                        jwtToken: this._keycloakService.getToken()!
                    };

                    if (sub.localSubscribeTime != null) {
                        const localDeltaMS = Date.now() - sub.localSubscribeTime;

                        if (sub.lastOrTimestampMS != null)
                            msg.lastOrTimestampMs = sub.lastOrTimestampMS - localDeltaMS - this.RESTORE_MARGIN_DELTA_MS;
                        else msg.lastOrTimestampMs = -1 * localDeltaMS - this.RESTORE_MARGIN_DELTA_MS;

                        sub.localSubscribeTime = null;
                    } else if (sub.lastOrTimestampMS != null) {
                        msg.lastOrTimestampMs = sub.lastOrTimestampMS - this.RESTORE_MARGIN_DELTA_MS;
                    }

                    this.send(msg);
                });
                break;

            case 'subscribed':
                {
                    const subscribedMsg = msg as Oo8EventServiceSuccessMessage;
                    if (subscribedMsg.data && this._subscriptions.has(subscribedMsg.data['channel'] as string)) {
                        const channel = this._subscriptions.get(subscribedMsg.data['channel'] as string)!;

                        // Update channel pointer
                        channel.lastOrTimestampMS = subscribedMsg.timestampMs;
                    }
                }
                break;

            case 'channel_data':
                {
                    const channelDataMsg = msg as Oo8EventServiceChannelDataMessage;

                    if (this._subscriptions.has(channelDataMsg.channel)) {
                        channelDataMsg.payload = JsonUtils.deserializeValue(channelDataMsg.payload as string);
                        const channel = this._subscriptions.get(channelDataMsg.channel)!;

                        // Update channel pointer
                        channel.lastOrTimestampMS = channelDataMsg.timestampMs;

                        // Handle duplicate messages
                        if (this._recentMessageIds.has(channelDataMsg.messageId)) {
                            break;
                        }
                        this._recentMessageIds.add(channelDataMsg.messageId);
                        if (this._recentMessageIds.size > this.MAX_RECENT_MESSAGES) {
                            const oldestMessageId = this._recentMessageIds.values().next().value;
                            if (oldestMessageId) {
                                this._recentMessageIds.delete(oldestMessageId);
                            }
                        }

                        channel.eventSubject.next(channelDataMsg);
                    }
                }
                break;

            case 'error':
                console.error(
                    '[Oo8EventService] Server error:',
                    (msg as Oo8EventServiceErrorMessage).error,
                    (msg as Oo8EventServiceErrorMessage).code
                );

                if (
                    ((msg as Oo8EventServiceErrorMessage).code === 'AUTH_FAILED' ||
                        (msg as Oo8EventServiceErrorMessage).code) === 'AUTH_REQUIRED'
                ) {
                    this._keycloakService.redirectToLogin();
                }

                break;
            default:
                console.log('[Oo8EventService] Received message:', msg);
        }
    }
    /**
     * Sends a message to the WebSocket server.
     * @param message - The message to send.
     */
    private send(message: Oo8EventServiceOutgoingMessage): void {
        if (this._socket$ && !this._socket$.closed) {
            this._socket$.next(message);
        }
    }
    /**
     * Disconnects from the WebSocket server.
     */
    private disconnect(): void {
        if (this._socket$) {
            this._socket$.complete();
            this._socket$ = null;
        }
    }
}

// ===============================================================
// ===============================================================

export interface Oo8EventServiceMessage {
    type: string;
}

export interface Oo8EventServiceAuthMessage extends Oo8EventServiceMessage {
    domain: string;
    jwtToken: string;
}

export interface Oo8EventServiceSubscribeMessage extends Oo8EventServiceMessage {
    channelName: string;
    jwtToken: string;
    lastOrTimestampMs?: number;
}

export interface Oo8EventServiceUnsubscribeMessage extends Oo8EventServiceMessage {
    channel: string;
}

export interface Oo8EventServiceChannelDataMessage extends Oo8EventServiceMessage {
    channel: string;
    messageId: string;
    payload: unknown;
    timestampMs: number;
}

export interface Oo8EventServiceSuccessMessage extends Oo8EventServiceMessage {
    message: string;
    data?: Record<string, unknown>;
    timestampMs: number;
}

export interface Oo8EventServiceErrorMessage extends Oo8EventServiceMessage {
    error: string;
    code: string | null;
}

export type Oo8EventServiceIncommingMessage =
    | Oo8EventServiceChannelDataMessage
    | Oo8EventServiceSuccessMessage
    | Oo8EventServiceErrorMessage;
export type Oo8EventServiceOutgoingMessage =
    | Oo8EventServiceAuthMessage
    | Oo8EventServiceSubscribeMessage
    | Oo8EventServiceUnsubscribeMessage;
